/**
 * Settings (`Ta`/`Tb`/`Tc`).
 *
 * Copyright (C) 2026 Overbounce contributors
 * Licensed under the GNU General Public License v2 or later. See LICENSE.
 *
 * R7 named it "deliberately small" over three panels -- Movement, Display,
 * HUD -- and two design updates have since reshaped that. The first filled
 * in three more: Controls (real keybind remapping, `input.ts`'s own
 * binds store), Audio (the master volume `?volume=` already drove, now with
 * a slider here too), and Player (name + model, replacing "Assets" -- the
 * loader is reachable a different way already and never needed a settings
 * panel).
 *
 * The second removed Movement outright. Physics, camera and tick rate are
 * properties of a COURSE, not of a player: a course is built for one
 * physics mode and one view, and the tick rate is a fixed constant here
 * anyway. Their override already had a real home in course select's own
 * picker -- the same `PreferenceStore` keyed by map -- so Movement was a
 * second door onto one setting, and the door that could not show a levelshot
 * or a record beside it. Five panels now: Display, HUD, Controls, Audio,
 * Player.
 *
 * R8: nothing here reloads the page. A reload would drop every `.pk3` a
 * player mounted in memory, forcing them to re-select the lot -- the actual
 * complaint that made this screen's original "every control reloads"
 * design wrong. Three contexts, three behaviours:
 *
 * - **No course running** (`context` absent -- reached from the title
 *   screen before "Run a course"): a storage write and a re-render of this
 *   same panel. Nothing is consuming these options yet, so there is nothing
 *   to apply.
 * - **Mid-course, live** (`context.live` present -- reached only through
 *   PAUSED's "All settings"): obhelp/ghost/debugpanel/strafegauge/crosshair apply
 *   immediately through the SAME callbacks PAUSED's own QUICK SETTINGS panel
 *   uses -- `main.ts` builds one bundle and hands it to both, so the two are
 *   one mechanism, not two that could disagree (crosshair itself has no row
 *   in QUICK SETTINGS, only in the full HUD tab, but the live-apply path is
 *   shared regardless). The seven pure post-processing
 *   Display effects (tonemap/shadows/worldshadows/ssao/aberration/motionblur/lavabloom/lavashimmer/fxaa)
 *   rebuild the render chain in place the same way, through
 *   `context.live.onPostSettingChange`.
 * - **Mid-course, baked** (`context.live` present, but Shadows, Water or Fog
 *   softness):
 *   these are compiled into world-mesh materials once at course start, so
 *   there is no live path for them yet. Storage write, and a "takes effect
 *   next time it starts" hint for the identical reason -- honest, not
 *   disruptive.
 *
 * Every panel here writes `LocalSettingsStore`. Nothing writes
 * `PreferenceStore` any more; that store is per-map, and course select owns
 * it -- see the Movement note above.
 */

import { createShell, createButton, createSegmentedControl, createToggle, createDropdown, createSlider } from '../shell.js';
import { DEFAULT_SENSITIVITY } from '../../input/input.js';
import type { Shell } from '../shell.js';
import { isFaithfulMode, applyRenderPreset, FAITHFUL_QUERY } from '../render-preset.js';
import { playFaithfulStingOnce } from '../faithful-sting.js';
import { LocalSettingsStore, SETTING_KEYS, stripUrlParam } from '../local-settings.js';
import type { SettingKey } from '../local-settings.js';
import type { ObHelpMode } from '../../render/hud.js';
import { crosshairSvg, NUM_CROSSHAIRS, OB_DEFAULT_CROSSHAIR } from '../../render/crosshair.js';
import { listPlayerModels } from '../../render/md3-mesh.js';
import type { Pk3FileSystem } from '../../assets/pk3.js';
import {
  ACTIONS,
  ACTION_LABEL,
  bindFromKeyboardEvent,
  bindFromMouseEvent,
  bindLabel,
  clearElsewhere,
  KeyBindsStore,
} from '../../input/keybinds.js';
import type { Bind, Binds } from '../../input/keybinds.js';

/**
 * The live half of R8's mid-course context -- shared verbatim with PAUSED's
 * own QUICK SETTINGS panel (`hud.ts`'s `HudCallbacks`; `main.ts` builds one
 * set of functions and passes it to both). Each HUD setter is expected to
 * both persist to storage AND apply the change, same as its pause-panel
 * counterpart; `onPostSettingChange` takes no value because there is no
 * per-key equivalent -- it just means "re-read storage and rebuild the
 * chain," after this screen has already written whichever key changed.
 */
export interface SettingsLiveCallbacks {
  onObHelpChange(mode: ObHelpMode): void;
  onGhostToggle(enabled: boolean): void;
  onDebugToggle(enabled: boolean): void;
  onStrafeGaugeToggle(enabled: boolean): void;
  /** The helper line is its own switch, not a mode of the gauge -- see the
   *  HUD panel's own note. */
  onStrafeHelperToggle(enabled: boolean): void;
  onCrosshairChange(style: number): void;
  /** Quake's `cg_drawGun`. First person only -- see the HUD panel's row. */
  onViewWeaponToggle(enabled: boolean): void;
  /** Percent 0-100. Same one-shot persist-and-apply shape as the others --
   *  see the Audio panel's own slider for why there is no separate
   *  live-while-dragging callback (Display's sliders set the precedent). */
  onVolumeChange(percent: number): void;
  /** Mute is its own flag, not "volume 0" -- see `local-settings.ts`'s
   *  `muted` key. The caller restores the real stored volume on unmute. */
  onMuteChange(muted: boolean): void;
  /** The start lines -- what this game says where Quake says "FIGHT!". Gates
   *  only the spoken line; crossing the gate still starts the run. */
  onStartSoundsToggle(enabled: boolean): void;
  /** Both finish lines, the personal best set and the weighted attempt set --
   *  one switch, because "did I beat it" is not a thing to configure twice. */
  onFinishSoundsToggle(enabled: boolean): void;
  /** The player model's own `death1..3.wav`. Not the fall grunt, which is a
   *  landing rather than a death -- see `main.ts`'s `voice.fall`. */
  onDeathSoundsToggle(enabled: boolean): void;
  /** The overbounce sting. Gates the sound only -- the detector, its console
   *  line and the cooldown all still run, so a run is unchanged. */
  onObSoundsToggle(enabled: boolean): void;
  /** `input.ts`'s own `setBinds` -- rebinding applies to the live game
   *  instantly, same R8 "no reload" shape as every other live setting. */
  onBindsChange(binds: Binds): void;
  /** Quake's `cg_autoswitch`, narrowed to weapons the player was not already
   *  carrying -- see `main.ts`'s `autoSwitchEnabled`. */
  onAutoSwitchChange(enabled: boolean): void;
  /** Q3's `sensitivity` cvar. Same one-shot persist-and-apply shape as the
   *  volume slider above. */
  onSensitivityChange(value: number): void;
  onPostSettingChange(): void;
}

export interface SettingsContext {
  mapName: string;
  /** Present only while a course is actually running -- see file header. */
  live?: SettingsLiveCallbacks;
  /** Whatever paks the running course has mounted, for the Player panel's
   *  model list -- `listPlayerModels` needs a real `Pk3FileSystem` to answer
   *  "what models does THIS pak set actually have". `null` when no paks are
   *  mounted (a bare `?map=` dev load). */
  paks: Pk3FileSystem | null;
}

type Tab = 'display' | 'hud' | 'controls' | 'audio' | 'player';

const STYLE = `
.ob-set-title { display:flex; align-items:baseline; gap:10px; font:600 20px/1 var(--ob-font-display);
  letter-spacing:.06em; text-transform:uppercase; }
.ob-set-desc { margin-top:9px; max-width:56ch; font:400 14px/1.5 var(--ob-font-display);
  letter-spacing:.03em; color:var(--ob-dim); }
.ob-set-row { display:flex; align-items:flex-start; justify-content:space-between; gap:30px; }
/* Rows stacked in one card had NOTHING between them, while a row's own
   description sits 9px under its own title. Spacing within a group was
   therefore larger than spacing between groups, which is the one thing
   proximity may not do: "description, then the next title" read as a unit,
   and every toggle looked like it answered the text above it rather than
   beside it. Adjacent-sibling on purpose -- a card holding one row is
   untouched, and so are Display (a card per effect) and Controls (its own
   row class). */
.ob-set-row + .ob-set-row { margin-top:20px; }
.ob-set-hint { font:400 10px/1 var(--ob-font-mono); letter-spacing:.06em; color:var(--ob-unavailable); }
.ob-set-crosshair { flex:none; display:flex; align-items:center; gap:12px; }
.ob-set-crosshair-preview { flex:none; width:28px; height:28px; color:var(--ob-text); }
.ob-set-crosshair-preview svg { width:100%; height:100%; display:block; }

.ob-set-presets { display:flex; gap:16px; }
.ob-set-preset { flex:1; padding:18px 20px; border:1px solid var(--ob-seam); border-radius:5px;
  background:var(--ob-panel); cursor:pointer; text-align:left; font:inherit; color:inherit; }
.ob-set-preset.active { border-color:var(--ob-control-hover); background:var(--ob-panel-alt-1); }
.ob-set-preset-head { display:flex; align-items:center; justify-content:space-between; }
.ob-set-preset-name { font:600 19px/1 var(--ob-font-display); letter-spacing:.08em; text-transform:uppercase;
  color:var(--ob-dim); }
.ob-set-preset.active .ob-set-preset-name { color:var(--ob-text); }
.ob-set-dot { width:16px; height:16px; border-radius:50%; border:1px solid var(--ob-control-hover); }
.ob-set-preset.active .ob-set-dot { border:4px solid var(--ob-accent); background:var(--ob-background); }
.ob-set-preset-desc { margin-top:10px; font:400 13px/1.45 var(--ob-font-display); letter-spacing:.03em;
  color:var(--ob-dim); }

.ob-set-volume { flex:none; display:flex; align-items:center; gap:14px; }
.ob-set-mute { flex:none; width:22px; height:22px; border-radius:4px; border:1px solid var(--ob-control-hover);
  background:transparent; color:var(--ob-dim); font:600 11px/1 var(--ob-font-mono); cursor:pointer; }
.ob-set-mute.active { border-color:var(--ob-accent); color:var(--ob-accent); background:rgba(232,98,42,.14); }

.ob-set-sens { flex:none; display:flex; align-items:center; gap:11px; }
/* The editable box IS this row's readout, so the slider's own label would be
   the same number printed twice. Scoped to this row -- volume keeps its. */
.ob-set-sens .ob-slider-value { display:none; }
.ob-set-sens-input { width:66px; padding:6px 8px; border:1px solid var(--ob-control-hover);
  border-radius:4px; background:var(--ob-panel-alt-1); color:var(--ob-text);
  font:600 12px/1 var(--ob-font-mono); letter-spacing:.04em; text-align:right;
  box-sizing:border-box; }
.ob-set-sens-input:focus { outline:none; border-color:var(--ob-accent); }

.ob-set-name-input { margin-top:14px; width:320px; max-width:100%; padding:11px 14px;
  border:1px solid var(--ob-control-hover); border-radius:4px; background:var(--ob-panel-alt-1);
  color:var(--ob-text); font:500 14px/1 var(--ob-font-mono); letter-spacing:.03em; box-sizing:border-box; }

.ob-set-model-grid { margin-top:16px; display:grid; grid-template-columns:repeat(6,1fr); gap:14px; }
.ob-set-model { display:flex; flex-direction:column; gap:8px; padding:0; border:none; background:none;
  cursor:pointer; font:inherit; color:inherit; text-align:left; }
.ob-set-model-swatch { aspect-ratio:1; border-radius:5px; border:1px solid var(--ob-control);
  background:repeating-linear-gradient(135deg,#1b1b23 0 8px,#20202a 8px 16px); }
.ob-set-model.active .ob-set-model-swatch { border:2px solid var(--ob-accent); }
.ob-set-model-label { text-align:center; font:400 12px/1 var(--ob-font-mono); letter-spacing:.05em;
  color:var(--ob-dim); }
.ob-set-model.active .ob-set-model-label { font-weight:600; color:var(--ob-accent); }

.ob-set-controls { display:flex; flex-direction:column; }
.ob-set-controls-row { display:flex; align-items:center; gap:12px; padding:11px 4px;
  border-top:1px solid var(--ob-seam); }
.ob-set-controls-row.head { border-top:none; padding-bottom:10px; font:400 10px/1 var(--ob-font-mono);
  letter-spacing:.14em; color:var(--ob-unavailable); }
.ob-set-controls-row.head span:first-child { flex:1; }
.ob-set-controls-row.head span:not(:first-child) { width:110px; }
.ob-set-controls-action { flex:1; font:500 15px/1 var(--ob-font-display); letter-spacing:.04em; }
.ob-set-bind { width:110px; padding:6px 10px; border:1px solid var(--ob-control-hover); border-radius:4px;
  background:var(--ob-panel-alt-1); color:var(--ob-text); font:600 12px/1 var(--ob-font-mono);
  letter-spacing:.05em; text-align:center; cursor:pointer; }
.ob-set-bind.capturing { border-color:var(--ob-accent); background:rgba(232,98,42,.14); color:var(--ob-accent); }
.ob-set-controls-reset { width:50px; text-align:right; font:400 11px/1 var(--ob-font-mono);
  color:var(--ob-unavailable); cursor:pointer; }
.ob-set-controls-reset:hover { color:var(--ob-dim); }
.ob-set-controls-footer { margin-top:18px; display:flex; align-items:center; justify-content:space-between;
  padding-top:18px; border-top:1px solid var(--ob-seam); gap:20px; }
`;

let styleInstalled = false;
function installStyle(): void {
  if (styleInstalled) {
    return;
  }
  const style = document.createElement('style');
  style.textContent = STYLE;
  document.head.appendChild(style);
  styleInstalled = true;
}

function el(tag: string, className?: string): HTMLElement {
  const e = document.createElement(tag);
  if (className) {
    e.className = className;
  }
  return e;
}

function card(): HTMLElement {
  return el('div', 'ob-card');
}

/** Display's three-way preset: Faithful and Modern are exact, everything
 *  else (including a page that has never touched a render param) is Custom
 *  -- there is no fourth state where "some of each" reads as one preset. */
const MODERN_DEFAULTS: Record<string, string> = {
  tonemap: 'agx',
  ssao: 'world',
  aberration: '0.1',
  motionblur: '0.2',
  lavabloom: '1',
  lavashimmer: '0.007',
  fogfeather: '1',
  fog: 'volumetric',
  shadows: 'lights',
  worldshadows: '1',
  water: 'modern',
  trail: 'modern',
  fxaa: '1',
};
function isModernMode(params: URLSearchParams): boolean {
  return Object.entries(MODERN_DEFAULTS).every(([k, v]) => (params.get(k) ?? v) === v);
}

/**
 * `backLabel` names where closing this screen actually lands, because that
 * is a different place from each of the three doors into it: the title menu,
 * course select, and PAUSED. The frames all say "Back to courses" -- that is
 * the one route the designer drew, not a claim about the other two -- so
 * each caller passes its own destination instead of inheriting that one.
 */
export function showSettingsScreen(
  parent: HTMLElement,
  context?: SettingsContext,
  backLabel = '← Back',
): Promise<void> {
  installStyle();

  const settings = new LocalSettingsStore();
  /** The merged view every panel below reads from -- storage filled in,
   *  a URL value (an explicit override, or a shared diagnostic link) always
   *  winning over it. See `local-settings.ts`. */
  const currentParams = (): URLSearchParams =>
    settings.withDefaults(new URLSearchParams(window.location.search));
  const controller = new AbortController();
  let tab: Tab = 'display';
  // While a Controls bind slot is armed and waiting for the next key/mouse
  // press, Escape has to cancel THAT instead of closing the whole screen --
  // the outer Escape listener near the bottom of this function checks this.
  let capturingBind = false;
  /**
   * Assigned by the Promise executor at the bottom, which runs synchronously
   * before this function returns and long before anything can click the back
   * button. The button and Escape have to be the SAME exit -- one that aborts
   * the listener, unmounts the shell and resolves -- and `finish` cannot be
   * hoisted out of the executor without hoisting `resolve` with it.
   */
  let close = (): void => {};

  const shell: Shell = createShell(parent, {
    sectionLabel: 'SETTINGS',
    items: [
      { id: 'display', label: 'Display' },
      { id: 'hud', label: 'HUD' },
      { id: 'controls', label: 'Controls' },
      { id: 'audio', label: 'Audio' },
      { id: 'player', label: 'Player' },
    ],
    activeId: tab,
    title: 'Display',
    // No status line: the frames replaced the per-tab hints that used to sit
    // here with the back button, and ESC is now printed beside it.
    onNavigate: (id) => {
      tab = id as Tab;
      shell.setTitle(TAB_TITLE[tab]);
      render();
    },
    // Unlike Escape, a click on this is unambiguous even mid-rebind: it can
    // only mean "leave", where Escape at that moment means "cancel the bind
    // I just armed". So it does NOT consult `capturingBind`.
    onBack: () => close(),
    backLabel,
  });

  const TAB_TITLE: Record<Tab, string> = {
    display: 'Display',
    hud: 'HUD',
    controls: 'Controls',
    audio: 'Audio',
    player: 'Player',
  };

  /**
   * Persist first, then apply -- exactly `applyDisplaySetting` above.
   *
   * This used to be `if (liveApply) liveApply(); else persist;`, on the
   * reasoning that a live callback persists on its own (they all end in
   * `applyQuickSetting`). That held for the door it was written against --
   * a running course -- and silently failed at the other one. `main.ts`
   * opens this same screen over PLAYBACK, where a row with nothing to apply
   * to a recording is passed `() => {}`; the write then went to the no-op
   * instead of to storage and the change was dropped on the floor. Six rows
   * were affected (obhelp, ghost, debugpanel, strafegauge, strafehelper,
   * crosshair) and the failure was invisible: the panel re-rendered from
   * storage, so the control snapped back and read as "the toggle is broken".
   *
   * Writing unconditionally makes the callback's job purely to APPLY, which
   * is the only thing a caller can fail to do correctly. `liveApply` still
   * persists the same value a second time -- it must, because PAUSED's QUICK
   * SETTINGS panel calls those functions directly without coming through
   * here -- and a repeated identical write costs nothing.
   */
  const applyHudSetting = (key: SettingKey, value: string | null, liveApply?: () => void): void => {
    settings.set(key, value);
    stripUrlParam(key);
    liveApply?.();
    render();
  };

  /**
   * The nine Display keys. There is no per-key live setter for these the
   * way HUD has -- `onPostSettingChange` takes no value, so the write always
   * happens here first, then the trigger (when a course is running) re-reads
   * storage whole and rebuilds. Also correct for Shadows/Water, which have
   * no live path at all: those callers below skip passing `live` through.
   */
  const applyDisplaySetting = (key: SettingKey, value: string | null, live?: () => void): void => {
    settings.set(key, value);
    stripUrlParam(key);
    live?.();
    render();
  };

  const effectRow = (title: string, desc: string, control: HTMLElement): HTMLElement => {
    const c = card();
    const row = el('div', 'ob-set-row');
    const text = el('div');
    const t = el('div', 'ob-set-title');
    t.textContent = title;
    const d = el('div', 'ob-set-desc');
    d.textContent = desc;
    text.append(t, d);
    row.append(text, control);
    c.appendChild(row);
    return c;
  };

  // ---- Display ----
  const renderDisplay = (): void => {
    const params = currentParams();
    const faithful = isFaithfulMode(params);
    const modern = !faithful && isModernMode(params);
    const mode: 'modern' | 'faithful' | 'custom' = faithful ? 'faithful' : modern ? 'modern' : 'custom';

    const presets = el('div', 'ob-set-presets');
    const preset = (
      id: 'modern' | 'faithful' | 'custom',
      name: string,
      desc: string,
      onClick: (() => void) | null,
    ): void => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ob-set-preset' + (mode === id ? ' active' : '');
      const head = el('div', 'ob-set-preset-head');
      const label = el('div', 'ob-set-preset-name');
      label.textContent = name;
      const dot = el('div', 'ob-set-dot');
      head.append(label, dot);
      const d = el('div', 'ob-set-preset-desc');
      d.textContent = desc;
      btn.append(head, d);
      if (onClick) {
        btn.addEventListener('click', onClick);
      }
      presets.appendChild(btn);
    };
    const applyPreset = (faithfulPreset: boolean): void => {
      applyRenderPreset(settings, faithfulPreset);
      for (const pair of FAITHFUL_QUERY.split('&')) {
        stripUrlParam(pair.split('=')[0]);
      }
      // The same once-ever sting the title screen's two-way switch fires --
      // one function, so finding Faithful here and finding it there are the
      // same discovery and not two.
      playFaithfulStingOnce(faithfulPreset);
      // The recipe touches Shadows and Water too, which have no live path --
      // `onPostSettingChange` still rebuilds the six it can (tonemap/ssao/
      // aberration/motionblur/lavabloom/lavashimmer; fxaa is outside the
      // recipe, see `render-preset.ts`), and the hint under the presets
      // covers the rest.
      context?.live?.onPostSettingChange();
      render();
    };
    preset('modern', 'Modern', 'AgX tone mapping, SSAO, FXAA, a real shadow map, lava bloom and heat shimmer — all deliberately gentle.', () => applyPreset(false));
    preset('faithful', 'Faithful 1999', 'What Quake actually drew: no tone curve, no ambient occlusion, no aberration, no motion blur, and Quake’s own blob shadow.', () => applyPreset(true));
    preset('custom', 'Custom', 'Each effect set individually below — stored, and remembered next time regardless of what started this page.', null);
    const presetHint = el('div', 'ob-set-hint');
    if (context?.live) {
      presetHint.style.marginTop = '-4px';
      presetHint.textContent = 'shadows and water take effect next time this course starts — everything else above is immediate';
    }

    const toneRow = effectRow(
      'Tone mapping',
      'The curve from linear light to a displayable image. Off is what Quake actually drew — no curve at all. AgX is the deliberately gentle modern default.',
      createDropdown(
        [
          { id: 'off', label: 'Off' },
          { id: 'agx', label: 'AgX' },
          { id: 'neutral', label: 'Neutral' },
          { id: 'aces', label: 'ACES' },
          { id: 'cineon', label: 'Cineon' },
          { id: 'reinhard', label: 'Reinhard' },
        ],
        (params.get('tonemap') ?? MODERN_DEFAULTS.tonemap).toLowerCase() === 'none'
          ? 'off'
          : (params.get('tonemap') ?? MODERN_DEFAULTS.tonemap).toLowerCase(),
        (id) =>
          applyDisplaySetting('tonemap', id === MODERN_DEFAULTS.tonemap ? null : id, context?.live?.onPostSettingChange),
      ),
    );

    const shadowsRow = effectRow(
      'Shadows',
      'From lights casts from the map’s own lamps and from rockets and plasma — lights that are actually somewhere. ' +
        'Steered sun casts from a directional light aimed by the baked light grid instead: it works on a map with no lamps, ' +
        'but its direction is sampled at the player’s own position and leans as you move, so the map’s shadows swing with you. ' +
        'Blob is Quake’s own flat ellipse under every entity.' +
        (context?.live
          ? ` Baked into the world when it loads — ${context.mapName} keeps running its current shadows; the choice below takes effect next time it starts.`
          : ''),
      createDropdown(
        [
          { id: 'lights', label: 'From lights' },
          { id: 'dynamic', label: 'Steered sun' },
          { id: 'blob', label: 'Blob' },
          { id: 'off', label: 'Off' },
        ],
        params.get('shadows') ?? MODERN_DEFAULTS.shadows,
        // No `live` argument -- shadows are baked, not post-processing; see
        // the file header's "mid-course, baked" case.
        (id) => applyDisplaySetting('shadows', id === MODERN_DEFAULTS.shadows ? null : id),
      ),
    );

    const worldOn = (params.get('worldshadows') ?? MODERN_DEFAULTS.worldshadows) !== '0';
    const worldShadowsRow = effectRow(
      'World shadows',
      'Lets the map itself cast, not just the players and items on it — a wall lamp’s cone stops at a pillar, and the steered sun swings a shadow across the floor as you move. ' +
        'Costs a second pass over the world per casting light: measured on q3dm6 under four wall lamps, 188 draws to 398 and 57k triangles to 253k. Needs Shadows on Dynamic.' +
        (context?.live
          ? ` Baked into the world when it loads — ${context.mapName} keeps running as it is; the choice below takes effect next time it starts.`
          : ''),
      createToggle(worldOn, () =>
        // No `live` argument -- `castShadow` is set on each world mesh as it is
        // built, the same "baked, not post-processing" case as Shadows above.
        applyDisplaySetting('worldshadows', worldOn ? null : '1'),
      ),
    );

    const ssaoRow = effectRow(
      'Ambient occlusion',
      'World masks the effect to map geometry, so a spinning item does not shimmer as its own occlusion changes.',
      createDropdown(
        [
          { id: 'world', label: 'World' },
          { id: 'all', label: 'All' },
          { id: 'off', label: 'Off' },
        ],
        params.get('ssao') ?? MODERN_DEFAULTS.ssao,
        (id) => applyDisplaySetting('ssao', id === MODERN_DEFAULTS.ssao ? null : id, context?.live?.onPostSettingChange),
      ),
    );

    const waterRow = effectRow(
      'Water',
      'Modern refracts what is behind the surface. Faithful is the flat, undistorted reference picture.' +
        (context?.live
          ? ` Baked into the world when it loads — ${context.mapName} keeps running its current water; the choice below takes effect next time it starts.`
          : ''),
      createDropdown(
        [
          { id: 'modern', label: 'Modern' },
          { id: 'faithful', label: 'Faithful' },
        ],
        params.get('water') ?? MODERN_DEFAULTS.water,
        // No `live` argument -- same reason as shadows, above.
        (id) => applyDisplaySetting('water', id === MODERN_DEFAULTS.water ? null : id),
      ),
    );

    const trailRow = effectRow(
      'Rocket trail',
      'Modern raymarches a noise field inside each puff, so the smoke has interior structure that churns and thins as it ages. ' +
        'Faithful is Quake’s own: a sprite of gfx/misc/smokepuff3.tga every 50ms, growing from 8 to 72 units while it fades over two seconds. ' +
        'Off is cg_noProjectileTrail.',
      createDropdown(
        [
          { id: 'modern', label: 'Modern' },
          { id: 'faithful', label: 'Faithful' },
          { id: 'off', label: 'Off' },
        ],
        params.get('trail') ?? MODERN_DEFAULTS.trail,
        // No `live` argument -- the pool's materials are built once, per mode.
        (id) => applyDisplaySetting('trail', id === MODERN_DEFAULTS.trail ? null : id),
      ),
    );

    const fxaaOn = (params.get('fxaa') ?? MODERN_DEFAULTS.fxaa) !== '0';
    const fxaaRow = effectRow(
      'FXAA',
      'A pass render target does not carry the canvas’s own antialiasing, so this is what restores smoothed edges once the chain is on at all.',
      createToggle(fxaaOn, () => applyDisplaySetting('fxaa', fxaaOn ? '0' : null, context?.live?.onPostSettingChange)),
    );

    const lavabloomRow = effectRow(
      'Lava bloom',
      'Bloom strength on lit-from-within lava surfaces. 0 removes the stage entirely.',
      createSlider(
        0,
        1,
        0.05,
        Number(params.get('lavabloom') ?? MODERN_DEFAULTS.lavabloom),
        () => {},
        (v) =>
          applyDisplaySetting(
            'lavabloom',
            v === Number(MODERN_DEFAULTS.lavabloom) ? null : String(v),
            context?.live?.onPostSettingChange,
          ),
      ),
    );

    const lavashimmerRow = effectRow(
      'Heat shimmer',
      'Peak heat-haze displacement over lava, in UV units. 0 removes the stage — anything under ~0.003 was tried and found invisible.',
      createSlider(
        0,
        0.02,
        0.001,
        Number(params.get('lavashimmer') ?? MODERN_DEFAULTS.lavashimmer),
        () => {},
        (v) =>
          applyDisplaySetting(
            'lavashimmer',
            v === Number(MODERN_DEFAULTS.lavashimmer) ? null : String(v),
            context?.live?.onPostSettingChange,
          ),
      ),
    );

    const fogRow = effectRow(
      'Fog',
      'Volumetric raymarches the map’s own fog brushes, so the fog has depth and drifts. ' +
        'Analytic is Quake’s flat per-surface tint. Only three of the shipped maps have fog brushes at all.' +
        (context?.live
          ? ` Baked into the world when it loads — ${context.mapName} keeps its current fog; the choice below takes effect next time it starts.`
          : ''),
      createDropdown(
        [
          { id: 'volumetric', label: 'Volumetric' },
          { id: 'analytic', label: 'Analytic' },
        ],
        params.get('fog') ?? MODERN_DEFAULTS.fog,
        // No `live` argument -- the march is compiled into the post chain
        // against the map's volumes, same as shadows and water are baked.
        (id) => applyDisplaySetting('fog', id === MODERN_DEFAULTS.fog ? null : id),
      ),
    );

    const fogfeatherRow = effectRow(
      'Fog softness',
      'How soft the boundary of a fog volume is, as a fraction of its own size. Volumetric fades in from every face; analytic fades down from the volume’s top. ' +
        'Quake measures fog along the view ray, and a side camera sits far enough back that the ray saturates ' +
        'almost at the surface — 0 is that unsoftened edge. 1 spreads the fade across the whole volume; ' +
        'above that it keeps spreading past the far side, which thins the fog as well as softening it.' +
        (context?.live
          ? ` Baked into the world when it loads — ${context.mapName} keeps running its current fog; the choice below takes effect next time it starts.`
          : ''),
      createSlider(
        0,
        // Past 1 on purpose, so the default is not the maximum. At 1 the fade
        // exactly spans the volume; beyond it the ramp runs off the far side,
        // which thins the fog as well as softening its edge -- a real thing to
        // want on a dense volume, and the only direction this slider could not
        // otherwise go.
        2,
        0.05,
        Number(params.get('fogfeather') ?? MODERN_DEFAULTS.fogfeather),
        () => {},
        // No `live` argument -- fog is compiled into the world material, the
        // same as shadows and water above.
        (v) =>
          applyDisplaySetting(
            'fogfeather',
            v === Number(MODERN_DEFAULTS.fogfeather) ? null : String(v),
          ),
      ),
    );

    const aberrationRow = effectRow(
      'Chromatic aberration',
      'Radial colour fringing toward the edge of the frame. 0 removes the stage; nothing at the crosshair either way.',
      createSlider(
        0,
        0.5,
        0.01,
        Number(params.get('aberration') ?? MODERN_DEFAULTS.aberration),
        () => {},
        (v) =>
          applyDisplaySetting(
            'aberration',
            v === Number(MODERN_DEFAULTS.aberration) ? null : String(v),
            context?.live?.onPostSettingChange,
          ),
      ),
    );

    const motionblurRow = effectRow(
      'Motion blur',
      'Streaks the frame along the direction of travel, ramping in above run speed and reaching full strength at 1200ups. 0 removes the stage; below 320ups it is already invisible.',
      createSlider(
        0,
        2,
        0.1,
        Number(params.get('motionblur') ?? MODERN_DEFAULTS.motionblur),
        () => {},
        (v) =>
          applyDisplaySetting(
            'motionblur',
            v === Number(MODERN_DEFAULTS.motionblur) ? null : String(v),
            context?.live?.onPostSettingChange,
          ),
      ),
    );

    shell.body.append(
      presets,
      presetHint,
      toneRow,
      shadowsRow,
      worldShadowsRow,
      ssaoRow,
      waterRow,
      trailRow,
      fxaaRow,
      lavabloomRow,
      lavashimmerRow,
      fogRow,
      fogfeatherRow,
      aberrationRow,
      motionblurRow,
    );
  };

  // ---- HUD ----
  const toggle = createToggle;

  const renderHud = (): void => {
    const params = currentParams();

    const obHelpCard = card();
    const obRow = el('div', 'ob-set-row');
    const obText = el('div');
    const obTitle = el('div', 'ob-set-title');
    obTitle.textContent = 'Overbounce help';
    const obDesc = el('div', 'ob-set-desc');
    obDesc.textContent =
      'Auto retires the explanation per method once you have landed that one cleanly twice. After that you get the letter, which is all a defrag player ever had.';
    obText.append(obTitle, obDesc);
    const obHelp = (params.get('obhelp') ?? 'auto').toLowerCase();
    const obSeg = createSegmentedControl(
      [
        { id: 'full', label: 'FULL' },
        { id: 'auto', label: 'AUTO' },
        { id: 'letter', label: 'LETTER' },
      ],
      ['full', 'auto', 'letter'].includes(obHelp) ? obHelp : 'auto',
      (id) => {
        const live = context?.live;
        applyHudSetting('obhelp', id === 'auto' ? null : id, live ? () => live.onObHelpChange(id as ObHelpMode) : undefined);
      },
    );
    obRow.append(obText, obSeg);
    obHelpCard.appendChild(obRow);

    const strafeCard = card();
    const strafeRow = el('div', 'ob-set-row');
    const strafeText = el('div');
    const strafeTitle = el('div', 'ob-set-title');
    strafeTitle.textContent = 'Strafe gauge';
    const strafeDesc = el('div', 'ob-set-desc');
    strafeDesc.textContent = 'Only ever shown airborne and above wishspeed — there is no window to hit on the ground.';
    strafeText.append(strafeTitle, strafeDesc);
    const strafeOn = (params.get('strafegauge') ?? '1') !== '0';
    strafeRow.append(
      strafeText,
      toggle(strafeOn, () => {
        const live = context?.live;
        applyHudSetting('strafegauge', strafeOn ? '0' : null, live ? () => live.onStrafeGaugeToggle(!strafeOn) : undefined);
      }),
    );
    strafeCard.appendChild(strafeRow);

    /*
     * The same information as the gauge, drawn as a distance instead of an
     * instrument -- so it is a separate switch rather than a mode of the
     * gauge: a player learning the window wants the numbers, a player who has
     * learnt it wants the line, and some want both.
     *
     * Off by default. It draws across the middle of the screen, which is a
     * bigger imposition than a row in the corner and not something to opt
     * somebody into.
     */
    const helperRow = el('div', 'ob-set-row');
    const helperText = el('div');
    const helperTitle = el('div', 'ob-set-title');
    helperTitle.textContent = 'Strafe helper line';
    const helperDesc = el('div', 'ob-set-desc');
    helperDesc.textContent =
      'A line from the crosshair to where your aim should be, as long as the turn you '
      + 'still owe. Disappears once you are within a flick of it. Same conditions as the '
      + 'gauge: airborne, above wishspeed.';
    helperText.append(helperTitle, helperDesc);
    const helperOn = (params.get('strafehelper') ?? '0') !== '0';
    helperRow.append(
      helperText,
      toggle(helperOn, () => {
        const live = context?.live;
        applyHudSetting(
          'strafehelper',
          helperOn ? null : '1',
          live ? () => live.onStrafeHelperToggle(!helperOn) : undefined,
        );
      }),
    );
    strafeCard.appendChild(helperRow);

    const debugCard = card();
    const debugRow = el('div', 'ob-set-row');
    const debugText = el('div');
    const debugTitle = el('div', 'ob-set-title');
    debugTitle.textContent = 'Debug panel';
    const debugDesc = el('div', 'ob-set-desc');
    debugDesc.textContent = 'Position, yaw, airtime, jump count, cpu, fps — top right. F3 toggles it in play. The coordinates are the ones a bug report wants.';
    debugText.append(debugTitle, debugDesc);
    const debugOn = (params.get('debugpanel') ?? '1') !== '0';
    debugRow.append(
      debugText,
      toggle(debugOn, () => {
        const live = context?.live;
        applyHudSetting('debugpanel', debugOn ? '0' : null, live ? () => live.onDebugToggle(!debugOn) : undefined);
      }),
    );
    debugCard.appendChild(debugRow);

    const ghostCard = card();
    const ghostRow = el('div', 'ob-set-row');
    const ghostText = el('div');
    const ghostTitle = el('div', 'ob-set-title');
    ghostTitle.textContent = 'Ghost';
    const ghostDesc = el('div', 'ob-set-desc');
    ghostDesc.textContent = 'Your best run, replayed through the same pmove — a real opponent, not an animation.';
    ghostText.append(ghostTitle, ghostDesc);
    const ghostOn = (params.get('ghost') ?? '1') !== '0';
    ghostRow.append(
      ghostText,
      toggle(ghostOn, () => {
        const live = context?.live;
        applyHudSetting('ghost', ghostOn ? '0' : null, live ? () => live.onGhostToggle(!ghostOn) : undefined);
      }),
    );
    ghostCard.appendChild(ghostRow);

    const crosshairCard = card();
    const crosshairRow = el('div', 'ob-set-row');
    const crosshairText = el('div');
    const crosshairTitle = el('div', 'ob-set-title');
    crosshairTitle.textContent = 'Crosshair';
    const crosshairDesc = el('div', 'ob-set-desc');
    crosshairDesc.textContent =
      'First person only — a side or chase view uses the aim laser instead, since it shows where the shot lands in the world. One of Quake III’s ten styles, or off.';
    crosshairText.append(crosshairTitle, crosshairDesc);

    const crosshairControl = el('div', 'ob-set-crosshair');
    const crosshairPreview = el('div', 'ob-set-crosshair-preview');
    const rawCrosshair = Number(params.get('crosshair') ?? OB_DEFAULT_CROSSHAIR);
    const crosshairValue = Number.isFinite(rawCrosshair) ? Math.max(0, Math.trunc(rawCrosshair)) : OB_DEFAULT_CROSSHAIR;
    crosshairPreview.innerHTML = crosshairValue > 0 ? crosshairSvg(crosshairValue) : '';
    const crosshairOptions = [
      { id: '0', label: 'Off' },
      ...Array.from({ length: NUM_CROSSHAIRS }, (_, i) => {
        const n = i + 1;
        return { id: String(n), label: n === OB_DEFAULT_CROSSHAIR ? `${n} (default)` : String(n) };
      }),
    ];
    const crosshairDropdown = createDropdown(crosshairOptions, String(crosshairValue), (id) => {
      const style = Number(id);
      const live = context?.live;
      // `applyHudSetting` re-renders this whole panel, which rebuilds the
      // preview from the new stored value -- no need to update it here too.
      applyHudSetting(
        'crosshair',
        style === OB_DEFAULT_CROSSHAIR ? null : String(style),
        live ? () => live.onCrosshairChange(style) : undefined,
      );
    });
    crosshairControl.append(crosshairPreview, crosshairDropdown);
    crosshairRow.append(crosshairText, crosshairControl);
    crosshairCard.appendChild(crosshairRow);

    /*
     * Quake's `cg_drawGun`, and it belongs beside the crosshair rather than in
     * Display: both are things drawn for the player rather than things done to
     * the picture, and both are first person only. On by default, as the cvar
     * is -- the people who turn it off are turning off a sixth of the screen,
     * which is a preference worth having and not one to assume.
     */
    const gunRow = el('div', 'ob-set-row');
    const gunText = el('div');
    const gunTitle = el('div', 'ob-set-title');
    gunTitle.textContent = 'View weapon';
    const gunDesc = el('div', 'ob-set-desc');
    gunDesc.textContent =
      'The gun in your own hands, with Quake III’s bob, landing dip and idle drift. First person only — a side or chase view already shows the weapon the player model is holding.';
    gunText.append(gunTitle, gunDesc);
    const gunOn = !['0', 'off'].includes((params.get('gun') ?? '1').toLowerCase());
    gunRow.append(
      gunText,
      toggle(gunOn, () => {
        const live = context?.live;
        applyHudSetting(
          'gun',
          gunOn ? '0' : null,
          live ? () => live.onViewWeaponToggle(!gunOn) : undefined,
        );
      }),
    );
    crosshairCard.appendChild(gunRow);

    shell.body.append(obHelpCard, strafeCard, debugCard, ghostCard, crosshairCard);
  };

  // ---- Audio (Td2) ----
  const renderAudio = (): void => {
    const params = currentParams();
    const volume = Math.max(0, Math.min(100, Number(params.get('volume') ?? '60')));
    const muted = (params.get('muted') ?? '0') !== '0';

    const c = card();
    const row = el('div', 'ob-set-row');
    const text = el('div');
    const title = el('div', 'ob-set-title');
    title.textContent = 'Master volume';
    const desc = el('div', 'ob-set-desc');
    desc.textContent =
      'Everything — sfx, music, UI — routes through one gain node for now. Muted persists across reloads.';
    text.append(title, desc);

    const control = el('div', 'ob-set-volume');
    const muteBtn = document.createElement('button');
    muteBtn.type = 'button';
    muteBtn.className = 'ob-set-mute' + (muted ? ' active' : '');
    muteBtn.textContent = 'M';
    muteBtn.title = muted ? 'Unmute' : 'Mute';
    muteBtn.addEventListener('click', () => {
      const live = context?.live;
      applyHudSetting('muted', muted ? null : '1', live ? () => live.onMuteChange(!muted) : undefined);
    });

    const slider = createSlider(0, 100, 1, volume, () => {}, (v) => {
      const live = context?.live;
      applyHudSetting('volume', v === 60 ? null : String(v), live ? () => live.onVolumeChange(v) : undefined);
    });

    control.append(muteBtn, slider);
    row.append(text, control);
    c.appendChild(row);
    shell.body.appendChild(c);

    /*
     * The spoken lines, separately from the volume they play at.
     *
     * These are the only sounds in the game that are not Quake's -- recorded
     * for this project, and the ones a player hears the same handful of over
     * and over across an evening of attempts. Muting is the wrong instrument
     * for "I have heard `cute` enough": it takes the footsteps and the
     * overbounce sting with it. So each set gets its own switch, on by
     * default, and turning one off changes nothing about the run itself.
     */
    const voiceCard = card();

    const voiceRow = (
      key: 'startsounds' | 'finishsounds' | 'deathsounds' | 'obsounds',
      name: string,
      description: string,
      apply: (live: SettingsLiveCallbacks, enabled: boolean) => void,
    ): HTMLElement => {
      const vRow = el('div', 'ob-set-row');
      const vText = el('div');
      const vTitle = el('div', 'ob-set-title');
      vTitle.textContent = name;
      const vDesc = el('div', 'ob-set-desc');
      vDesc.textContent = description;
      vText.append(vTitle, vDesc);
      const on = (params.get(key) ?? '1') !== '0';
      vRow.append(
        vText,
        toggle(on, () => {
          const live = context?.live;
          applyHudSetting(key, on ? '0' : null, live ? () => apply(live, !on) : undefined);
        }),
      );
      return vRow;
    };

    voiceCard.appendChild(
      voiceRow(
        'startsounds',
        'Start lines',
        'What this game says where Quake says “FIGHT!” — one of three, at the top of every attempt.',
        (live, enabled) => live.onStartSoundsToggle(enabled),
      ),
    );
    voiceCard.appendChild(
      voiceRow(
        'finishsounds',
        'Finish lines',
        'Crossing the finish, whether or not you beat your own time — the personal best set and the ones for everything else.',
        (live, enabled) => live.onFinishSoundsToggle(enabled),
      ),
    );
    voiceCard.appendChild(
      voiceRow(
        'deathsounds',
        'Death sounds',
        'Your player model’s own death cry. The grunt on a hard landing is a fall, not a death, and stays either way.',
        (live, enabled) => live.onDeathSoundsToggle(enabled),
      ),
    );
    voiceCard.appendChild(
      voiceRow(
        'obsounds',
        'Overbounce announcements',
        'The sting when a landing turns fall speed into speed — the mechanic the game is named after. It already holds its tongue for three seconds after each one.',
        (live, enabled) => live.onObSoundsToggle(enabled),
      ),
    );
    shell.body.appendChild(voiceCard);
  };

  // ---- Player (Te) ----
  const renderPlayer = (): void => {
    const params = currentParams();

    const nameCard = card();
    const nameTitle = el('div', 'ob-set-title');
    nameTitle.textContent = 'Name';
    const nameDesc = el('div', 'ob-set-desc');
    nameDesc.textContent = 'Shown on the leaderboard and above your ghost.';
    const nameInput = document.createElement('input');
    nameInput.className = 'ob-set-name-input';
    nameInput.type = 'text';
    nameInput.maxLength = 24;
    nameInput.value = params.get('playername') ?? '';
    nameInput.addEventListener('change', () => {
      const value = nameInput.value.trim();
      settings.set('playername', value ? value : null);
      stripUrlParam('playername');
    });
    nameCard.append(nameTitle, nameDesc, nameInput);

    const modelCard = card();
    const modelTitle = el('div', 'ob-set-title');
    modelTitle.textContent = 'Model';
    const modelDesc = el('div', 'ob-set-desc');
    modelDesc.textContent = 'Purely visual — every model shares the same hitbox and pmove.';
    modelCard.append(modelTitle, modelDesc);

    const current = params.get('player') ?? '';
    const models = context?.paks ? listPlayerModels(context.paks) : null;
    if (models && models.length) {
      const grid = el('div', 'ob-set-model-grid');
      for (const name of models) {
        const cell = document.createElement('button');
        cell.type = 'button';
        cell.className = 'ob-set-model' + (name === current ? ' active' : '');
        const swatch = el('div', 'ob-set-model-swatch');
        const label = el('div', 'ob-set-model-label');
        label.textContent = name;
        cell.append(swatch, label);
        cell.addEventListener('click', () => {
          settings.set('player', name);
          stripUrlParam('player');
          render();
        });
        grid.appendChild(cell);
      }
      modelCard.appendChild(grid);
      const hint = el('div', 'ob-set-hint');
      hint.textContent = `${context!.mapName} is running "${current || 'the default'}" — takes effect next time it starts`;
      modelCard.appendChild(hint);
    } else {
      const hint = el('div', 'ob-set-hint');
      hint.textContent = current
        ? `currently "${current}" — open from a course to pick from what its paks actually have`
        : 'open from a course to pick a model — its own paks decide what is available';
      modelCard.appendChild(hint);
    }

    shell.body.append(nameCard, modelCard);
  };

  // ---- Controls (Td) ----
  const renderControls = (): void => {
    const params = currentParams();
    const kb = new KeyBindsStore();
    let binds = kb.read();

    const applyBinds = (next: Binds): void => {
      binds = next;
      kb.write(binds);
      context?.live?.onBindsChange(binds);
    };

    const c = card();
    const table = el('div', 'ob-set-controls');
    const head = el('div', 'ob-set-controls-row head');
    for (const label of ['ACTION', 'BIND 1', 'BIND 2', '']) {
      const cell = el('span');
      cell.textContent = label;
      head.appendChild(cell);
    }
    table.appendChild(head);

    for (const action of ACTIONS) {
      const row = el('div', 'ob-set-controls-row');
      const nameCell = el('span', 'ob-set-controls-action');
      nameCell.textContent = ACTION_LABEL[action];
      row.appendChild(nameCell);

      for (const slot of [0, 1] as const) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ob-set-bind';
        btn.textContent = bindLabel(binds[action][slot]);
        btn.addEventListener('click', () => {
          if (capturingBind) {
            return;
          }
          capturingBind = true;
          btn.classList.add('capturing');
          btn.textContent = 'PRESS A KEY…';

          const finishCapture = (bind: Bind | 'cancel'): void => {
            window.removeEventListener('keydown', onKey, true);
            window.removeEventListener('mousedown', onMouse, true);
            window.removeEventListener('contextmenu', onContext, true);
            capturingBind = false;
            if (bind !== 'cancel') {
              const next = binds[action].slice() as [Bind, Bind];
              next[slot] = bind;
              applyBinds(clearElsewhere({ ...binds, [action]: next }, bind, action, slot));
            }
            render();
          };
          const onKey = (e: KeyboardEvent): void => {
            e.preventDefault();
            // `stopImmediatePropagation`, not just `stopPropagation`: both
            // this listener and the outer Escape-closes-the-screen listener
            // near the bottom of this function are registered on the SAME
            // node (`window`), so ordinary `stopPropagation` does not stop
            // a sibling listener on that same node from still running --
            // only `stopImmediatePropagation` does. Confirmed live: without
            // this, pressing Escape to cancel a capture also closed the
            // whole settings screen a moment later, because by the time the
            // outer listener ran, `capturingBind` (set below) was already
            // back to `false`.
            e.stopImmediatePropagation();
            finishCapture(e.code === 'Escape' ? 'cancel' : bindFromKeyboardEvent(e));
          };
          const onMouse = (e: MouseEvent): void => {
            e.preventDefault();
            finishCapture(bindFromMouseEvent(e));
          };
          const onContext = (e: MouseEvent): void => e.preventDefault();
          // Capture phase, so this beats the canvas/page's own handlers --
          // and `true` here is unrelated to a mouse button's role as a bind.
          window.addEventListener('keydown', onKey, true);
          window.addEventListener('mousedown', onMouse, true);
          window.addEventListener('contextmenu', onContext, true);
        });
        row.appendChild(btn);
      }

      const reset = el('span', 'ob-set-controls-reset');
      reset.textContent = 'reset';
      reset.addEventListener('click', () => {
        applyBinds(kb.resetOne(action));
        render();
      });
      row.appendChild(reset);
      table.appendChild(row);
    }
    c.appendChild(table);

    const footer = el('div', 'ob-set-controls-footer');
    const hint = el('span', 'ob-set-hint');
    hint.textContent = 'click a bind, then press any key or mouse button — esc cancels';
    const resetAll = createButton('Reset all to defaults', 'ghost');
    resetAll.addEventListener('click', () => {
      applyBinds(kb.resetAll());
      render();
    });
    footer.append(hint, resetAll);
    c.appendChild(footer);

    shell.body.appendChild(c);

    /*
     * Weapon auto switch. Here rather than in HUD because it is about what a
     * keypress you did not make does -- the same thing every row above it is
     * about.
     *
     * `applyHudSetting` despite the name: it is the generic
     * "persist-or-live-apply, then re-render" path, and the alternative is a
     * second copy of it. The live callback persists on its own (see
     * `applyQuickSetting` in `main.ts`), which is exactly why the storage
     * write only happens on the branch without one.
     */
    const autoCard = card();
    const autoRow = el('div', 'ob-set-row');
    const autoText = el('div');
    const autoTitle = el('div', 'ob-set-title');
    autoTitle.textContent = 'Weapon auto switch';
    const autoDesc = el('div', 'ob-set-desc');
    autoDesc.textContent =
      'Picking up a weapon you were not already carrying equips it. Only a new one: '
      + 'walking back over a launcher you already have leaves your hands alone, so a '
      + 'respawned pickup on the route cannot take the rocket out of them mid-run.';
    autoText.append(autoTitle, autoDesc);
    const autoOn = (params.get('autoswitch') ?? '1') !== '0';
    autoRow.append(
      autoText,
      toggle(autoOn, () => {
        const live = context?.live;
        applyHudSetting(
          'autoswitch',
          autoOn ? '0' : null,
          live ? () => live.onAutoSwitchChange(!autoOn) : undefined,
        );
      }),
    );
    autoCard.appendChild(autoRow);
    shell.body.appendChild(autoCard);

    /*
     * Mouse. One number, and it is Quake's own.
     *
     * `sensitivity` rather than a 0-100 slider, because the turn per mouse
     * count is `m_yaw(0.022) * sensitivity` exactly as it is in Q3 -- so a
     * player who knows their number from a decade of Quake types it in and is
     * home. Range 0.5-15 covers the sane span either side of the default 5;
     * the URL param accepts up to 30 for anyone who really wants it.
     *
     * There is no acceleration toggle to sit beside it. Pointer lock is taken
     * with `unadjustedMovement`, so the OS curve is already out of the way and
     * an option to put it back would be an option to make aiming less
     * repeatable -- see `input.ts`.
     */
    const mouse = card();
    const sensRow = el('div', 'ob-set-row');
    const mouseText = el('div');
    const mouseTitle = el('div', 'ob-set-title');
    mouseTitle.textContent = 'Sensitivity';
    const mouseDesc = el('div', 'ob-set-desc');
    mouseDesc.textContent =
      'Quake 3’s own number, default 5 — the turn per mouse count is m_yaw ' +
      '× sensitivity exactly as it is there, so bring yours with you. Acceleration ' +
      'is always off and has no switch: this game asks you to repeat a turn, and an ' +
      'acceleration curve makes the same flick turn a different amount.';
    mouseText.append(mouseTitle, mouseDesc);

    /*
     * 0.01 at the bottom, not 0.5 (owner-directed). A player on a very
     * high-DPI mouse wants a number well under 1, and the old floor put those
     * out of reach entirely.
     *
     * The step has to come down with the floor. A range input counts its
     * steps FROM the minimum, so 0.01 with a step of 0.5 would offer 0.01,
     * 0.51, 1.01 ... and the default of 5 would not be among them -- the one
     * value everybody needs would be the one value unreachable by dragging.
     */
    const SENS_MIN = 0.01;
    const SENS_MAX = 15;
    const SENS_STEP = 0.01;
    /** The step's own precision, which also sweeps up the float dust a range
     *  input hands back (5 arriving as 5.000000000000001). */
    const round2 = (v: number): number => Math.round(v * 100) / 100;

    const stored = Number(params.get('sensitivity'));
    const current = Number.isFinite(stored) && stored > 0 ? stored : DEFAULT_SENSITIVITY;
    // Captured once for EVERY handler below, rather than per-click the way the
    // toggles above do it: this row has three entry points and they have to
    // agree about which door this screen was opened from. Nothing can go
    // stale -- `applyHudSetting` re-renders, which rebuilds this row.
    const live = context?.live;

    /*
     * Apply on every drag tick, WRITE only on release or on a typed value.
     *
     * Live while dragging because "does this feel right" is the whole
     * question and cannot be answered from a number.
     *
     * But the drag handler must not be the thing that stores it, which is
     * what this used to be: BOTH handlers called `context?.live?.…` and
     * nothing else, so the only door that remembered a new sensitivity was
     * a running course (whose callback persists on its own). Over playback
     * the callback is a no-op stub and from the title screen `live` is
     * undefined, so `?.` swallowed the call whole -- the readout moved, the
     * setting was never written, and it was back to 5 on the next render.
     *
     * Commit goes through `applyHudSetting` like every other control, which
     * persists first and applies second. That fixes all three doors at once
     * and costs one write per drag rather than one per mouse move: a write
     * is a read-modify-write of the whole blob plus a `history.replaceState`,
     * which is not something to do at input-event rate.
     */
    const commitSens = (v: number): void => {
      const value = round2(Math.min(SENS_MAX, Math.max(SENS_MIN, v)));
      applyHudSetting(
        'sensitivity',
        value === DEFAULT_SENSITIVITY ? null : String(value),
        live ? () => live.onSensitivityChange(value) : undefined,
      );
    };

    /*
     * A box to type into, because a slider cannot be asked for 2.73 and a
     * player moving between games is carrying an exact number, not a feel.
     * It is the row's readout as well -- `createSlider`'s own label is hidden
     * here rather than removed, since volume still wants one.
     */
    const sensBox = document.createElement('input');
    sensBox.className = 'ob-set-sens-input';
    sensBox.type = 'text';
    sensBox.inputMode = 'decimal';
    sensBox.spellcheck = false;
    sensBox.setAttribute('aria-label', 'Sensitivity');
    // The TRUE stored value, which `?sensitivity=` may have put above
    // SENS_MAX -- the parameter's own ceiling is 30, and the URL is an
    // override the UI does not get to quietly overrule. The slider beside it
    // clamps, because a slider has nowhere to put a number it cannot reach;
    // showing 15 HERE for a setting that is really 20 would be a lie.
    sensBox.value = String(round2(current));
    sensBox.addEventListener('change', () => {
      const typed = Number(sensBox.value.trim());
      if (!Number.isFinite(typed) || typed <= 0) {
        // Put back, and write nothing. Clamping junk to the floor would mean
        // a typo silently becomes a real setting -- and 0.01 is the one value
        // a player is least likely to have meant.
        sensBox.value = String(round2(current));
        return;
      }
      commitSens(typed);
    });

    const sensControl = el('div', 'ob-set-sens');
    sensControl.append(
      createSlider(
        SENS_MIN,
        SENS_MAX,
        SENS_STEP,
        Math.min(SENS_MAX, Math.max(SENS_MIN, current)),
        (v) => {
          // The box tracks the drag, or the two would disagree mid-gesture.
          sensBox.value = String(round2(v));
          live?.onSensitivityChange(v);
        },
        commitSens,
      ),
      sensBox,
    );
    sensRow.append(mouseText, sensControl);
    mouse.appendChild(sensRow);
    shell.body.appendChild(mouse);
  };

  const render = (): void => {
    shell.body.innerHTML = '';
    switch (tab) {
      case 'display':
        renderDisplay();
        break;
      case 'hud':
        renderHud();
        break;
      case 'controls':
        renderControls();
        break;
      case 'audio':
        renderAudio();
        break;
      case 'player':
        renderPlayer();
        break;
    }
  };
  render();

  const resetBtn = createButton('Reset to defaults', 'ghost');
  // Clears every SETTING_KEYS entry from storage -- not a blanket wipe of the
  // URL, which would also drop ?map=/?devpak=/?at= and anything else
  // diagnostic there for an unrelated reason. Also strips the same keys from
  // the current URL in case one is sitting there as a stale override, same
  // as every other write in this file. No reload (R8): applies live through
  // the same `context.live` bundle every individual control above uses.
  resetBtn.addEventListener('click', () => {
    for (const key of SETTING_KEYS) {
      settings.set(key, null);
      stripUrlParam(key);
    }
    const live = context?.live;
    if (live) {
      live.onObHelpChange('auto');
      live.onGhostToggle(true);
      live.onDebugToggle(true);
      live.onStrafeGaugeToggle(true);
      live.onStartSoundsToggle(true);
      live.onFinishSoundsToggle(true);
      live.onDeathSoundsToggle(true);
      live.onPostSettingChange();
    }
    render();
  });

  const copyBtn = createButton('Copy URL', 'ghost');
  copyBtn.addEventListener('click', () => {
    // Settings live in storage now, not the URL -- copying `location.href`
    // verbatim would silently drop everything storage is holding, breaking
    // "a setting and a bug report are the same string"
    // (`docs/url-parameters.md`). This writes every SETTING_KEYS value the
    // page is actually running under (storage, URL override or hardcoded
    // default -- `currentParams()` already resolved which) explicitly into
    // the copied URL, so pasting it elsewhere reproduces this exact state
    // with no local storage of its own required.
    const url = new URL(window.location.href);
    const effective = currentParams();
    for (const key of SETTING_KEYS) {
      const value = effective.get(key);
      if (value !== null) {
        url.searchParams.set(key, value);
      }
    }
    void navigator.clipboard.writeText(url.toString()).then(
      () => {
        const original = copyBtn.textContent;
        copyBtn.textContent = 'Copied';
        setTimeout(() => {
          copyBtn.textContent = original;
        }, 1200);
      },
      () => {
        console.warn('[overbounce] clipboard write failed');
      },
    );
  });

  shell.footerLeft.appendChild(resetBtn);
  shell.footerRight.appendChild(copyBtn);

  return new Promise((resolve) => {
    const finish = (): void => {
      controller.abort();
      shell.dispose();
      resolve();
    };
    close = finish;
    window.addEventListener(
      'keydown',
      (e) => {
        if (e.code === 'Escape' && !capturingBind) {
          finish();
        }
      },
      { signal: controller.signal },
    );
  });
}
